/**
 * store/pult.ts — the pult's own slice: what its screen shows, and where its knobs and faders
 * stand (task 54).
 *
 * There is no magnifier and no window onto the board any more (`.omc/wiki/pult-variant-b.md`):
 * every finger is a control's, and the board is always whole. The key the windows were remembered
 * under (`fdm:pult-view`) is left on the phone as it is; nobody reads it.
 *
 * - **The tabs** (`tabs`, variant B): which tab each part of the board shows — VINYL, PADS or LOOP
 *   on each deck, MIXER, FX or SCREEN on the mixer. Each part's its own; every opening of the pult
 *   starts at VINYL · MIXER · VINYL ({@link resetTabs}), and they are not remembered on the phone.
 *   They are what the board shows, not what it sounds: no tab touches `values`, `held` or the pads.
 * - **The screen** (`screen`) shows the queue, the catalogue or the two decks; `chosen` is the
 *   track picked on it, which LOAD lays on a deck (task 55).
 * - **AUTO** (`auto`, task 56): the queue goes on by itself, remembered on the phone
 *   ({@link PULT_AUTO_KEY}); on by default — the player as it always was.
 * - **The hand** (`held`, task 56): the controls a finger is on — the automat leaves them alone.
 * - **The effect** (`effect`, task 58): which of the four keys is lit — one for both decks, ECHO at
 *   every launch, as the device's at power-on; not remembered on the phone. **PRESET** (`preset`): the automat's way with the
 *   effects (`lib/pult-presets.ts`), remembered on the phone ({@link PULT_PRESET_KEY}); `clean` by
 *   default — a transition as it always was.
 * - **The pads** (`pads`, task 60): each deck's pad mode and the page PARAMETER turned to
 *   (`lib/pult-pads.ts`). HOT CUE on every launch, as the device's at power-on; not remembered on
 *   the phone. A deck's mode and page are its own, as on the device.
 * - **The controls** (`values`): where every knob, fader and paddle stands, 0…1 by the control's id
 *   (`lib/pult-layout.ts`). A knob: 0 is all the way left, 0,5 straight up. A fader: the share of
 *   its slot from the top, or from the left. A paddle: 0 down, 1 up. In task 54 they move under
 *   the finger and nothing sounds; tasks 55–58 read them for the sound. The tempo faders are not
 *   here since task 55: they are the decks' own tempos (`store/deck.ts`).
 */

import { create } from 'zustand';

import type { TrackItem } from '../lib/api';
import type { PultEffect } from '../lib/pult-effects';
import { DECK_TABS, MIXER_TABS } from '../lib/pult-layout';
import type {
  DeckSide,
  DeckTab,
  MixerTab,
  PadMode,
  PultSide,
  PultTab,
  SCREEN_VIEWS,
} from '../lib/pult-layout';
import { turnPage } from '../lib/pult-pads';
import type { PultPreset } from '../lib/pult-presets';

/** Task 56: where AUTO is remembered. */
export const PULT_AUTO_KEY = 'fdm:pult-auto';

/** Task 58: where PRESET is remembered. */
export const PULT_PRESET_KEY = 'fdm:pult-preset';

/** What the screen in the middle of the pult shows: the keys QUEUE, LIBRARY, VIEW. */
export type PultScreenView = (typeof SCREEN_VIEWS)[number][0];

/** Task 60: a deck's pads — the mode chosen, and the page of it PARAMETER turned to. */
export interface PultPads {
  readonly mode: PadMode;
  readonly page: number;
}

/** The tab each part of the board shows. */
export interface PultTabs {
  readonly A: DeckTab;
  readonly B: DeckTab;
  readonly mid: MixerTab;
}

export interface PultState {
  readonly tabs: PultTabs;
  readonly screen: PultScreenView;
  /** The track chosen on the screen, for LOAD. */
  readonly chosen: TrackItem | undefined;
  readonly values: Readonly<Record<string, number>>;
  /** Task 56: the controls a finger is on now — the automat writes none of them. */
  readonly held: ReadonlySet<string>;
  /** Task 56: AUTO — the queue goes on by itself, one track into the next. */
  readonly auto: boolean;
  /** Task 58: the effect the four keys chose. */
  readonly effect: PultEffect;
  /** Task 58: PRESET. */
  readonly preset: PultPreset;
  /** Task 60: each deck's pads. */
  readonly pads: Readonly<Record<DeckSide, PultPads>>;
}

/**
 * Where the controls stand on a pult nobody has touched. Task 56: the faders up, the crossfader at
 * deck A's side — the deck a launch plays on — and every knob in the middle: LEVEL at 0 dB, the
 * tone flat, the filter off. The player then sounds as it did before the mixer.
 */
const PULT_DEFAULTS: Readonly<Record<string, number>> = Object.freeze({
  'A.fader': 0,
  'B.fader': 0,
  xfader: 0,
  'A.fx': 0,
  'B.fx': 0,
});

/** A knob, fader or paddle with no default of its own stands in the middle. */
export function valueOf(values: Readonly<Record<string, number>>, id: string): number {
  return values[id] ?? PULT_DEFAULTS[id] ?? 0.5;
}

/** AUTO as the phone remembers it: on, unless it was turned off. */
function rememberedAuto(): boolean {
  try {
    return localStorage.getItem(PULT_AUTO_KEY) !== 'off';
  } catch {
    return true;
  }
}

/** PRESET as the phone remembers it: `clean`, unless DUB OUT was chosen. */
function rememberedPreset(): PultPreset {
  try {
    return localStorage.getItem(PULT_PRESET_KEY) === 'dubout' ? 'dubout' : 'clean';
  } catch {
    return 'clean';
  }
}

/** The tabs an opening of the pult shows: both platters and the mixer's channels. */
const OPENING_TABS: PultTabs = Object.freeze({ A: 'vinyl', B: 'vinyl', mid: 'mixer' });

/** Task 60: the pads as the device comes up — HOT CUE, its first page. */
const POWER_ON_PADS: PultPads = Object.freeze({ mode: 'hotcue', page: 0 });

function fresh(): PultState {
  return {
    tabs: OPENING_TABS,
    screen: 'queue',
    chosen: undefined,
    values: {},
    held: new Set(),
    auto: rememberedAuto(),
    effect: 'echo',
    preset: rememberedPreset(),
    pads: { A: POWER_ON_PADS, B: POWER_ON_PADS },
  };
}

export const usePult = create<PultState>(fresh);

function isDeckTab(tab: PultTab): tab is DeckTab {
  return DECK_TABS.some(([one]) => one === tab);
}

function isMixerTab(tab: PultTab): tab is MixerTab {
  return MIXER_TABS.some(([one]) => one === tab);
}

/** A tab of part `group` pressed; a tab not of that part changes nothing. */
export function chooseTab(group: PultSide, tab: PultTab): void {
  const { tabs } = usePult.getState();
  if (tabs[group] === tab) return;
  if (group === 'mid' && isMixerTab(tab)) usePult.setState({ tabs: { ...tabs, mid: tab } });
  if (group !== 'mid' && isDeckTab(tab)) usePult.setState({ tabs: { ...tabs, [group]: tab } });
}

/** The pult opens: VINYL · MIXER · VINYL, whatever was chosen the last time. */
export function resetTabs(): void {
  usePult.setState({ tabs: OPENING_TABS });
}

export function showOnScreen(screen: PultScreenView): void {
  usePult.setState({ screen });
}

export function chooseTrack(track: TrackItem | undefined): void {
  usePult.setState({ chosen: track });
}

/** A knob, fader or paddle moved to `value`, kept within 0…1. */
export function setValue(id: string, value: number): void {
  const within = Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0.5;
  usePult.setState((s) =>
    s.values[id] === within ? s : { values: { ...s.values, [id]: within } },
  );
}

/** Task 56: a finger landed on control `id`, or left it. */
export function setHeld(id: string, on: boolean): void {
  const held = usePult.getState().held;
  if (held.has(id) === on) return;
  const next = new Set(held);
  if (on) next.add(id);
  else next.delete(id);
  usePult.setState({ held: next });
}

/** Task 56: AUTO pressed. Remembered on the phone. */
export function toggleAuto(): void {
  const auto = !usePult.getState().auto;
  usePult.setState({ auto });
  try {
    localStorage.setItem(PULT_AUTO_KEY, auto ? 'on' : 'off');
  } catch {
    // Remembered for this launch only.
  }
}

/** Task 58: one of the four effect keys. */
export function chooseEffect(effect: PultEffect): void {
  if (usePult.getState().effect !== effect) usePult.setState({ effect });
}

/** Task 58: PRESET pressed — `clean` ↔ DUB OUT. Remembered on the phone. */
export function togglePreset(): void {
  const preset: PultPreset = usePult.getState().preset === 'dubout' ? 'clean' : 'dubout';
  usePult.setState({ preset });
  try {
    localStorage.setItem(PULT_PRESET_KEY, preset);
  } catch {
    // Remembered for this launch only.
  }
}

/** Task 60: a pad mode key of deck `side` — that mode, on its first page. */
export function choosePadMode(side: DeckSide, mode: PadMode): void {
  if (usePult.getState().pads[side].mode === mode) return;
  usePult.setState((s) => ({ pads: { ...s.pads, [side]: { mode, page: 0 } } }));
}

/** Task 60: PARAMETER of deck `side`, one page back (`-1`) or on (`1`), within its mode's pages. */
export function turnPadPage(side: DeckSide, direction: -1 | 1): void {
  const { mode, page } = usePult.getState().pads[side];
  const next = turnPage(mode, page, direction);
  if (next === page) return;
  usePult.setState((s) => ({ pads: { ...s.pads, [side]: { mode, page: next } } }));
}

/** Task 56: every finger gone — the pult closed under one: nothing stays held. */
export function releaseAll(): void {
  if (usePult.getState().held.size > 0) usePult.setState({ held: new Set() });
}

/** A launch again, for tests: AUTO and PRESET as the phone remembers them, nothing else. */
export function resetPult(): void {
  usePult.setState(fresh(), true);
}
